import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { Lock } from "lucide-react";

const FORBIDDEN_PINS = new Set(["000000", "123456"]);

export default function PinChangeOverlay() {
  const { t } = useTranslation();
  const { employee, refresh } = useAuth();
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!employee?.must_change_pin || success) return null;
  // Signed in from an email link before ever choosing a PIN: they were never
  // told the temporary one, so only ask for the new PIN.
  const firstPin = !!employee.pin_setup;

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (newPin.length !== 6 || !/^\d{6}$/.test(newPin)) {
      setError(t("pinOverlay.mustBe6Digits"));
      return;
    }
    if (FORBIDDEN_PINS.has(newPin)) {
      setError(t("pinOverlay.tooSimple"));
      return;
    }
    if (newPin !== confirmPin) {
      setError(t("auth.pinMismatch"));
      return;
    }

    setSubmitting(true);
    try {
      await api.put("/api/auth/change-pin", {
        ...(firstPin ? {} : { current_pin: currentPin }),
        new_pin: newPin,
      });
      setSuccess(true);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/80 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-pme-blue/10 text-pme-blue">
            <Lock size={26} strokeWidth={1.75} aria-hidden />
          </div>
          <h2 className="text-lg font-bold text-gray-900">
            {firstPin ? t("pinOverlay.setupTitle") : t("pinOverlay.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {firstPin ? t("pinOverlay.setupSubtitle") : t("pinOverlay.subtitle")}
          </p>
        </div>

        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          {!firstPin && (
          <label className="block text-sm">
            <span className="font-medium text-gray-700">{t("auth.currentPin")}</span>
            <input
              type="password"
              inputMode="numeric"
              maxLength={12}
              value={currentPin}
              onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
              className="input mt-1"
              autoFocus
            />
          </label>
          )}

          <label className="block text-sm">
            <span className="font-medium text-gray-700">{t("auth.newPin")}</span>
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
              className="input mt-1"
              autoFocus={firstPin}
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-gray-700">{t("auth.confirmPin")}</span>
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
              className="input mt-1"
            />
          </label>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full min-h-[48px] rounded-xl bg-pme-blue text-sm font-semibold text-white hover:bg-pme-blue/90 disabled:opacity-50"
          >
            {submitting ? t("common.loading") : t("pinOverlay.setPin")}
          </button>
        </form>
      </div>
    </div>
  );
}
